import { PricingItemTypeEnum } from '../../pricing-items/enums/pricing-item-type.enum';
import { allocateCents } from '../../../functions/allocate-cents.function';
import { fromCents } from '../../../functions/from-cents.function';
import { toCents } from '../../../functions/to-cents.function';
import { BuildRentalPricingStateInput } from '../interfaces/build-rental-pricing-state-input.interface';
import { RentalPricingState } from '../interfaces/rental-pricing-state.interface';
import { RentalPricingStateItem } from '../interfaces/rental-pricing-state-item.interface';
import { resolvePrintableDeposit } from './resolve-printable-deposit.function';
import { toEffectiveDiscountPercentage } from './to-effective-discount-percentage.function';
import { toRentalAccountStatement } from './to-rental-account-statement.function';

/**
 * Builds the one pricing shape from stored lines and totals.
 * Line discounts are allocated from those totals, never recomputed from a
 * category rate, so the lines add up to the footer that was saved.
 */
export function buildRentalPricingState(
  input: BuildRentalPricingStateInput,
): RentalPricingState {
  const eligibleIndexes = input.lines
    .map((line, index) =>
      line.type === PricingItemTypeEnum.STANDARD &&
      line.isStandardDiscountApplied
        ? index
        : -1,
    )
    .filter((index) => index >= 0);
  const weights = eligibleIndexes.map((index) =>
    Number(input.lines[index].unitsTotalNet ?? 0),
  );
  const discountNet = allocateCents(input.totals.computedDiscountNet, weights);
  const discountTax = allocateCents(input.totals.computedDiscountTax, weights);
  const discountGross = allocateCents(
    input.totals.computedDiscountGross,
    weights,
  );
  const allocated = new Map<
    number,
    { net: number; tax: number; gross: number }
  >();

  eligibleIndexes.forEach((lineIndex, shareIndex) => {
    allocated.set(lineIndex, {
      net: fromCents(discountNet[shareIndex] ?? 0),
      tax: fromCents(discountTax[shareIndex] ?? 0),
      gross: fromCents(discountGross[shareIndex] ?? 0),
    });
  });

  const items: RentalPricingStateItem[] = input.lines.map((line, index) => {
    const share = allocated.get(index) ?? { net: 0, tax: 0, gross: 0 };
    const unitsTotalTax = Number(line.unitsTotalTax ?? 0);
    const unitsTotalGross = Number(line.unitsTotalGross ?? 0);

    return {
      position: line.position,
      sourceUuid: line.sourceUuid,
      sourcePricingItemPresetId: line.sourcePricingItemPresetId,
      description: line.description,
      type: line.type,
      taxRateType: line.taxRateType,
      isTaxIncluded: line.isTaxIncluded ?? false,
      units: line.units,
      unitPrice: line.unitPrice,
      unitTaxRate: line.unitTaxRate,
      unitsTotalNet: line.unitsTotalNet,
      unitsTotalTax: line.unitsTotalTax,
      unitsTotalGross: line.unitsTotalGross,
      isStandardDiscountApplied: line.isStandardDiscountApplied ?? false,
      isCommissionEligible: line.isCommissionEligible ?? false,
      unitCommissionRate: line.unitCommissionRate,
      unitsTotalCommissionNet: line.unitsTotalCommissionNet,
      unitsTotalDiscountNet: share.net,
      unitsTotalDiscountTax: share.tax,
      unitsTotalDiscountGross: share.gross,
      unitsTotalTaxAfterDiscount: fromCents(
        toCents(unitsTotalTax) - toCents(share.tax),
      ),
      unitsTotalGrossAfterDiscount: fromCents(
        toCents(unitsTotalGross) - toCents(share.gross),
      ),
    };
  });

  const statement = toRentalAccountStatement(
    input.transactions,
    Number(input.totals.computedTotalGross ?? 0),
  );
  const deposit = resolvePrintableDeposit({
    depositFlatRate: input.context.depositFlatRate,
    depositPercentage: input.context.depositPercentage,
    discountedStandardGross: Number(input.totals.computedStandardGross ?? 0),
    percentageDepositRounding: input.context.percentageDepositRounding,
    transactions: input.transactions,
  });

  return {
    context: {
      ...input.context,
      effectiveDiscountPercentage: toEffectiveDiscountPercentage(
        input.totals.computedDiscountNet,
        input.totals.computedStandardNet,
      ),
    },
    items,
    totals: input.totals,
    deposit,
    statement: {
      totalCharged: statement.summary.totalCharged,
      totalPaid: statement.summary.totalPaid,
      balanceDue: statement.summary.balanceDue,
      payments: statement.paymentTransactions.map((payment) => ({
        sourceTransactionId: payment.id,
        transactedAt: payment.createdAt,
        code: payment.code,
        externalReference: payment.externalReference,
        type: payment.type,
        status: payment.status,
        description: payment.description,
        amount: payment.amount,
      })),
    },
  };
}
